import { useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Save, Type } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { CLIENT_SCREENS, SCREEN_LABEL_MAX, customScreenLabel, screenModuleOn } from '../../utils/clientScreens';

const selectCls = 'flex h-9 w-full rounded-md border border-input bg-input-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** Per-client names for the client portal sidebar, with a live preview of that sidebar. */
export function ScreenNamesTab() {
  const token = sessionStorage.get()?.token || '';
  const [clients, setClients] = useState<any[]>([]);
  const [clientId, setClientId] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getClients(token)
      .then((list: any[]) => {
        const sorted = [...(list || [])].sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setClients(sorted);
        setClientId((prev) => prev || sorted[0]?.id || '');
      })
      .catch((e: any) => toast.error(e.message || 'Could not load clients'))
      .finally(() => setLoading(false));
  }, [token]);

  const client = clients.find((c) => c.id === clientId);

  // Load the selected client's current names.
  useEffect(() => {
    if (!client) return;
    const current: Record<string, string> = {};
    for (const s of CLIENT_SCREENS) current[s.id] = customScreenLabel(client.themeConfig, s.id) || '';
    setNames(current);
    setSaved(current);
  }, [client]);

  const dirty = useMemo(() => CLIENT_SCREENS.some((s) => (names[s.id] || '').trim() !== (saved[s.id] || '').trim()), [names, saved]);
  const visible = CLIENT_SCREENS.filter((s) => screenModuleOn(client?.modules, s));
  const shownName = (id: string, fallback: string) => (names[id] || '').trim() || fallback;

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      // Merge into the latest theme config so other settings (colours, energy budget…) are kept.
      const fresh = await adminApi.getClient(token, client.id).catch(() => client);
      const screenLabels: Record<string, string> = {};
      for (const s of CLIENT_SCREENS) {
        const v = (names[s.id] || '').trim().slice(0, SCREEN_LABEL_MAX);
        if (v && v !== s.label) screenLabels[s.id] = v;
      }
      const themeConfig = { ...(fresh?.themeConfig || {}), screenLabels };
      await adminApi.updateClient(token, client.id, { themeConfig });
      setClients((list) => list.map((c) => (c.id === client.id ? { ...c, themeConfig } : c)));
      toast.success(`Screen names saved for ${client.name}`, { description: 'Their users see the new names after refreshing the portal.' });
    } catch (e: any) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex h-60 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading clients…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><Type className="h-6 w-6" /> Screen Names</h2>
          <p className="text-sm text-muted-foreground">Give each client its own names for the screens in their sidebar. Leave a field empty to keep the standard name.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className={`${selectCls} sm:w-[240px]`} value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Client">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button onClick={save} disabled={!client || saving || !dirty}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save names
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Sidebar screens</CardTitle>
            <CardDescription>Dimmed screens are not switched on for this client (Clients → Module Access); you can still name them in advance.</CardDescription>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {CLIENT_SCREENS.map((s) => {
              const on = screenModuleOn(client?.modules, s);
              const custom = (names[s.id] || '').trim();
              return (
                <div key={s.id} className={`flex flex-col gap-2 px-6 py-3 sm:flex-row sm:items-center ${on ? '' : 'opacity-55'}`}>
                  <div className="flex w-56 shrink-0 items-center gap-2.5">
                    <s.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{s.label}</div>
                      <div className="text-[11px] text-muted-foreground">{on ? 'Shown to this client' : 'Not switched on'}</div>
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <Input
                      value={names[s.id] || ''}
                      maxLength={SCREEN_LABEL_MAX}
                      placeholder={s.label}
                      onChange={(e) => setNames((n) => ({ ...n, [s.id]: e.target.value }))}
                      aria-label={`Name for ${s.label}`}
                    />
                    <Button type="button" variant="ghost" size="icon" title="Use the standard name" disabled={!custom}
                      onClick={() => setNames((n) => ({ ...n, [s.id]: '' }))}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Preview of the client's sidebar with the names as typed */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview · {client?.name || '—'}</p>
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="flex h-16 items-center justify-center border-b border-border/40 text-lg font-bold tracking-wide">FACTREON</div>
            <nav className="space-y-1.5 p-3">
              {visible.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">No screens are switched on for this client.</p>}
              {visible.map((s, i) => (
                <div key={s.id}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${i === 0 ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow' : 'text-muted-foreground'}`}>
                  <s.icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{shownName(s.id, s.label)}</span>
                  {i === 0 && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                </div>
              ))}
            </nav>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Users also need the matching permission to see a screen.</p>
        </div>
      </div>
    </div>
  );
}
